import React, { useEffect, forwardRef, cloneElement, createElement } from 'react';
import { useLockBodyScroll } from 'react-use';
import { useToggle, useConditionalEffect } from '@react-hookz/web';
import { _set } from '@monobits/core';
import { useLocomotiveScroll, LocomotiveScrollProvider as LocomotiveScrollProvider$1 } from 'react-locomotive-scroll';
export { useLocomotiveScroll } from 'react-locomotive-scroll';
import { pick, omit, throttle, merge } from 'lodash';
import { isFirefox } from 'react-device-detect';
/**
 * Use boolean to start or stop the scroll
 *
 * @param  {Object} scroll The scroll instance
 * @param  {boolean} bool Is the scroll locked
 *
 * @returns {function} Document pixel height (unitless)
 */

var lockScroll = function lockScroll(scroll, bool) {
  return bool ? scroll.start() : scroll.stop();
};
/**
 * A robust way to toggle the scroll lock state
 *
 * @param  {boolean} initialProps initial state of the boolean
 *
 * @example const [locked, toggleLocked] = useToggleLock(false)
 */


var useToggleLock = function useToggleLock(initialProps) {
  if (initialProps === void 0) {
    initialProps = false;
  }

  var _useToggle = useToggle(initialProps),
      locked = _useToggle[0],
      setLocked = _useToggle[1];

  useEffect(function () {
    return _set.data('scroll-locked', locked);
  }, [locked]);
  useLockBodyScroll(locked);

  var toggleLocked = function toggleLocked(bool) {
    lockScroll(bool);
    setLocked();
  };

  return [locked, toggleLocked];
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}
/**
 * @internal
 * Default `LocomotiveScroll` instance options.
 *
 * @type {Object}
 */


var DEFAULT_SETTINGS = {
  smooth: true,
  lerp: 0.15,
  multiplier: 1,
  class: '_inview',
  getDirection: true,
  getSpeed: true,
  repeat: false,
  resetNativeScroll: false,
  extends: {
    firefoxLevel: 2.5,
    rootPercent: 2,
    throttle: 80,
    scrub: false
  }
};
/**
 * @internal
 * Array containing all attributes available in `LocomotiveScroll`.
 *
 * @type {Array.<string>}
 */

var REGISTERED_ATTRIBUTES = ['speed', 'direction', 'container', 'section', 'offset', 'repeat', 'target', 'position', 'delay', 'sticky'];
var _excluded$2 = ["children", "wrapped"];
/**
 * Gets and transforms registered props into attributes
 *
 * @param  {Object} props Component props
 * @param  {Array} whitelist Registered props for attribute transformation
 *
 * @returns {Object} Returns props & attributes
 */

var _get = function _get(props, whitelist) {
  if (whitelist === void 0) {
    whitelist = [];
  }

  var options = [props, whitelist];
  return {
    attr: pick.apply(void 0, options),
    props: omit.apply(void 0, options)
  };
};
/**
 * Use Locomotive Scroll motion
 * Inject props and attribute to the children component
 *
 * @param  {boolean} props.wrapped If true, wraps the child in a div
 */


var Locomotion = function Locomotion(_ref, ref) {
  var children = _ref.children,
      _ref$wrapped = _ref.wrapped,
      wrapped = _ref$wrapped === void 0 ? false : _ref$wrapped,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$2);

  var _useLocomotiveScroll = useLocomotiveScroll(),
      scroll = _useLocomotiveScroll.scroll;

  var get = _get(props, REGISTERED_ATTRIBUTES);

  var attributes = Object.keys(get.attr).reduce(function (acc, key) {
    var _extends2;

    return _extends({}, acc, (_extends2 = {}, _extends2["data-scroll-" + key] = props[key], _extends2));
  }, {});
  useEffect(function () {
    return scroll && scroll.update();
  }, [props]); // eslint-disable-line

  var forward = _extends({
    'data-scroll': true
  }, attributes, get.props, {
    ref: ref
  });

  if (!wrapped) return /*#__PURE__*/cloneElement(children, forward);
  return /*#__PURE__*/createElement('div', forward, children);
};

var Locomotion$1 = /*#__PURE__*/forwardRef(Locomotion);
/**
 * Get the true document height for all devices
 *
 * @returns {number} Document pixel height (unitless)
 */

var getScrollInnerHeight = function getScrollInnerHeight() {
  return document.documentElement.scrollHeight - window.innerHeight;
};
/**
 * Get the page scroll percentage value
 *
 * @param  {number} y Y axis scroll value in pixel (unitless)
 * @returns {number} Scroll progress in percent (unitless)
 */


var getScrollPercent = function getScrollPercent(y) {
  return Math.round(y / getScrollInnerHeight() * 100);
};
/**
 * Get the page scroll position as a string
 *
 * @param  {number} y Y axis scroll value in pixel (unitless)
 * @param  {number} rootMargin Top & Bottom root margin in percent (unitless)
 * @returns {('top'|'bottom'|'scrolled')} Scroll position as a string
 */


var getScrollPosition = function getScrollPosition(y, rootMargin) {
  var percent = getScrollPercent(y);
  var is = {
    top: percent <= rootMargin,
    bottom: percent >= 100 - rootMargin
  };
  if (is.top) return 'top';
  if (is.bottom) return 'bottom';
  return 'scrolled';
};
/**
 * Adds interactive attribute to the HTML tag
 *
 * @param  {Object} scroll The scroll instance
 * @param  {function} onUpdate Event that trigger on scroll update
 * @param  {function} onMount Event that trigger on scroll mount
 *
 * @returns {function} Returns a set of functions
 */


var _onScroll = function _onScroll(scroll) {
  var _ref = scroll || DEFAULT_SETTINGS,
      _ref$extends = _ref.extends,
      throttle$1 = _ref$extends.throttle,
      rootPercent = _ref$extends.rootPercent,
      scrub = _ref$extends.scrub,
      getDirection = _ref.getDirection;

  if (scroll) {
    scroll.on('scroll', throttle(function (func) {
      var scroll = func.scroll,
          direction = func.direction;
      var scrubber = getScrollPercent(scroll.y);
      var scrollPosition = getScrollPosition(scroll.y, rootPercent);

      if (getDirection) {
        _set.data('direction', direction == null ? 'up' : direction); // Update `data-direction` with scroll direction.


        _set.data('scroll-position', scrollPosition); // Expose attribute for the top and bottom scroll positions

      }

      if (scrub) _set.cssvar('--scroll-scrub', scrubber); // Get PageScroll Percentage
    }, throttle$1));

    if (getDirection) {
      var resetDirection = function resetDirection() {
        return _set.data('direction', 'up');
      };

      resetDirection();
      setTimeout(function () {
        return resetDirection();
      }, 150);
    } // Forces the direction to reset on routeChange

  }
};
/**
 * USAGE EXAMPLE (scroll scrub):
 * transform: scaleX(calc(var(--scroll-scrub, 0) / 100)); // equals: 0.45
 * transform: translateX(calc(-100% + (1% * var(--scroll-scrub, 0)))); // equals: -65%
 */

/**
 * Extends Locomotive Scroll features with html attributes
 *
 * @example <LocomotiveExtends />
 */


var LocomotiveExtends = function LocomotiveExtends() {
  var _useLocomotiveScroll = useLocomotiveScroll(),
      scroll = _useLocomotiveScroll.scroll;

  useConditionalEffect(function () {
    return _onScroll(scroll);
  }, undefined, [scroll]);
  return null;
};
/**
 * Level out the speed value to compensate for Firefox being slower.
 *
 * @param  {number} multiplier=1 Initial value
 * @param  {number} level=2.5 Level value
 * @returns {number} The right speed value for the right browser
 *
 */


var _matchFirefox = function _matchFirefox(multiplier, level) {
  if (multiplier === void 0) {
    multiplier = 1;
  }

  if (level === void 0) {
    level = 2.5;
  }

  return Math.round((isFirefox ? level * multiplier : multiplier) * 100) / 100;
};

var _excluded$1 = ["multiplier"];
/**
 * Transform option and merge with default options
 * @param  {Object} options A set of locomotive options
 *
 * @returns {Object} Locomotive options object
 *
 * @example
 * _rewireOptions({
 *  smooth: true,
 *  lerp: 0.15,
 *  getDirection: true,
 *  extends: {
 *    scrub: false,
 *    rootPercent: 2,
 *  },
 * })
 */

var _rewireOptions = function _rewireOptions(options) {
  if (options === void 0) {
    options = {};
  }

  var firefoxLevel = DEFAULT_SETTINGS.extends.firefoxLevel;

  var _ref = options || DEFAULT_SETTINGS,
      multiplier = _ref.multiplier,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$1);

  return merge(DEFAULT_SETTINGS, _extends({
    multiplier: _matchFirefox(multiplier, firefoxLevel)
  }, props));
};

var _LocomotiveExtends;

var _excluded = ["children", "options", "location", "watch"];
/**
 * Rewired `react-locomotive-scroll` Provider,
 * passes logic on the options
 *
 * @param  {Array} watch All the dependencies you want to watch to update the scroll
 * @param  {Object} containerRef Scroll container ref
 * @param  {Object} options All available Locomotive Scroll instance options
 *
 * @example
 * <LocomotiveScrollProvider
 *  containerRef={ref}
 *  options={theme?.config?.locomotive}
 *  watch={[location]}
 *  // options={{
 *  //   smooth: true,
 *  //   lerp: 0.3
 *  // }}
 * >
 *  {children}
 * </LocomotiveScrollProvider>
 */

var LocomotiveScrollProvider = function LocomotiveScrollProvider(_ref) {
  var children = _ref.children,
      options = _ref.options,
      location = _ref.location,
      _ref$watch = _ref.watch,
      watch = _ref$watch === void 0 ? [] : _ref$watch,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  var callbacks = [location].concat(watch);
  return /*#__PURE__*/React.createElement(LocomotiveScrollProvider$1, _extends({
    options: _rewireOptions(options),
    location: location,
    watch: callbacks
  }, props), _LocomotiveExtends || (_LocomotiveExtends = /*#__PURE__*/React.createElement(LocomotiveExtends, null)), children);
};

export { Locomotion$1 as Locomotion, LocomotiveExtends, LocomotiveScrollProvider, getScrollInnerHeight, getScrollPercent, getScrollPosition, lockScroll, useToggleLock };